import { useState, useEffect } from 'react';
import { useAuth } from '../../../contexts/AuthContext';
import {
  collection,
  query,
  where,
  orderBy,
  onSnapshot,
  addDoc,
  updateDoc,
  deleteDoc,
  doc,
  Timestamp,
} from 'firebase/firestore';
import { db } from '../../../utils/init-firebase';
import { Issue, ActionItem, User, Rock, COLLECTION_NAMES } from '../types';

/**
 * Hook for managing Issues data with Firestore
 * Replaces Supabase queries with Firestore v9+ modular API
 * Also fetches related data: action items, users, and rocks
 */
export function useIssues(userId: string | undefined) {
  const [issues, setIssues] = useState<Issue[]>([]);
  const [actionItems, setActionItems] = useState<ActionItem[]>([]);
  const { eosUsers = [] } = useAuth();
  const [rocks, setRocks] = useState<Rock[]>([]);
  const [loading, setLoading] = useState(true);

  // Load rocks once (needed for rock name display)
  useEffect(() => {
    const rocksQuery = query(
      collection(db, COLLECTION_NAMES.ROCKS),
      orderBy('createdAt', 'desc')
    );
    const unsubscribe = onSnapshot(rocksQuery, (snapshot) => {
      const rocksData = snapshot.docs.map((doc) => {
        const data = doc.data();
        return {
          id: doc.id,
          ...data,
          dueDate: data.dueDate?.toDate?.() || data.dueDate,
          createdAt: data.createdAt || Timestamp.now(),
          updatedAt: data.updatedAt || Timestamp.now(),
        } as Rock;
      });
      setRocks(rocksData);
    }, (error) => {
      console.error('Error loading rocks:', error);
    });

    return () => unsubscribe();
  }, []);

  // Load action items linked to issues
  useEffect(() => {
    const actionItemsQuery = query(
      collection(db, COLLECTION_NAMES.ACTION_ITEMS),
      where('dropped', '==', false),
      orderBy('createdAt', 'desc')
    );

    const unsubscribe = onSnapshot(
      actionItemsQuery,
      (snapshot) => {
        const actionItemsData = snapshot.docs.map((doc) => {
          const data = doc.data();
          return {
            id: doc.id,
            ...data,
            description: data.description || data.task || '',
            assignedToUserId: data.assignedToUserId || data.assigneeId,
            linkedIssueId: data.linkedIssueId || data.issueId,
            dueDate: data.dueDate?.toDate?.() || data.dueDate,
            completedDate: data.completedDate?.toDate?.() || data.completedDate,
            createdAt: data.createdAt || Timestamp.now(),
            updatedAt: data.updatedAt || Timestamp.now(),
          } as ActionItem;
        });
        // Filter to only include action items linked to issues
        setActionItems(actionItemsData.filter((item) => item.linkedIssueId));
      },
      (error) => {
        console.error('Error loading action items:', error);
      }
    );

    return () => unsubscribe();
  }, []);

  // Load issues with real-time listener
  // Filter: Load all open/in-progress issues + resolved issues from last 30 days
  useEffect(() => {
    if (!userId) {
      setLoading(false);
      return;
    }

    const thirtyDaysAgo = Timestamp.fromDate(new Date(Date.now() - 30 * 24 * 60 * 60 * 1000));

    // Query 1: Open and in-progress issues (status != 'solved')
    const openIssuesQuery = query(
      collection(db, COLLECTION_NAMES.ISSUES),
      where('status', 'in', ['open', 'in-progress']),
      orderBy('createdAt', 'desc')
    );

    // Query 2: Resolved issues from last 30 days
    // Note: This requires a Firestore composite index on (status, solvedAt)
    // Firestore will provide a link to create the index if it doesn't exist
    const resolvedIssuesQuery = query(
      collection(db, COLLECTION_NAMES.ISSUES),
      where('status', '==', 'solved'),
      where('solvedAt', '>=', thirtyDaysAgo),
      orderBy('solvedAt', 'desc')
    );

    let openIssuesData: Issue[] = [];
    let resolvedIssuesData: Issue[] = [];
    let openLoaded = false;
    let resolvedLoaded = false;

    const updateIssues = () => {
      if (openLoaded && resolvedLoaded) {
        // Combine and deduplicate by ID
        const allIssues = [...openIssuesData, ...resolvedIssuesData];
        const uniqueIssues = Array.from(
          new Map(allIssues.map(issue => [issue.id, issue])).values()
        );
        // Sort by createdAt descending
        uniqueIssues.sort((a, b) => {
          const aDate = a.createdAt?.toDate ? a.createdAt.toDate() : (a.createdAt instanceof Date ? a.createdAt : new Date(0));
          const bDate = b.createdAt?.toDate ? b.createdAt.toDate() : (b.createdAt instanceof Date ? b.createdAt : new Date(0));
          return bDate.getTime() - aDate.getTime();
        });
        setIssues(uniqueIssues);
        setLoading(false);
      }
    };

    const unsubscribeOpen = onSnapshot(
      openIssuesQuery,
      (snapshot) => {
        openIssuesData = snapshot.docs.map((doc) => {
          const data = doc.data();
          return {
            id: doc.id,
            ...data,
            // Convert Firestore Timestamps to Date objects
            createdAt: data.createdAt || Timestamp.now(),
            updatedAt: data.updatedAt || Timestamp.now(),
            solvedAt: data.solvedAt?.toDate?.() || data.solvedAt,
            firstOpenedDate: data.firstOpenedDate || Timestamp.now(),
          } as Issue;
        });
        openLoaded = true;
        updateIssues();
      },
      (error) => {
        console.error('Error loading open issues:', error);
        openLoaded = true;
        updateIssues();
      }
    );

    const unsubscribeResolved = onSnapshot(
      resolvedIssuesQuery,
      (snapshot) => {
        resolvedIssuesData = snapshot.docs.map((doc) => {
          const data = doc.data();
          return {
            id: doc.id,
            ...data,
            // Convert Firestore Timestamps to Date objects
            createdAt: data.createdAt || Timestamp.now(),
            updatedAt: data.updatedAt || Timestamp.now(),
            solvedAt: data.solvedAt?.toDate?.() || data.solvedAt,
            firstOpenedDate: data.firstOpenedDate || Timestamp.now(),
          } as Issue;
        });
        resolvedLoaded = true;
        updateIssues();
      },
      (error) => {
        console.error('Error loading resolved issues:', error);
        resolvedLoaded = true;
        updateIssues();
      }
    );

    return () => {
      unsubscribeOpen();
      unsubscribeResolved();
    };
  }, [userId]);

  const createIssue = async (formData: {
    name: string;
    description: string;
    priority?: 'low' | 'medium' | 'high';
    status?: 'open' | 'in-progress' | 'solved';
    ownerId?: string;
    relatedRockId?: string;
    relatedMetricId?: string;
    isRock?: boolean;
    isMetric?: boolean;
    isCompany?: boolean;
    assignedToUserId?: string;
    departmentId?: string;
  }) => {
    if (!userId) return;

    try {
      const now = Timestamp.now();
      const issueData = {
        name: formData.name,
        description: formData.description,
        priority: formData.priority || 'medium',
        status: formData.status || 'open',
        ownerId: formData.ownerId || userId,
        relatedRockId: formData.relatedRockId || null,
        relatedMetricId: formData.relatedMetricId || null,
        isRock: formData.isRock || false,
        isMetric: formData.isMetric || false,
        isCompany: formData.isCompany || false,
        assignedToUserId: formData.assignedToUserId?.trim() || null,
        departmentId: formData.departmentId?.trim() || null,
        firstOpenedDate: now,
        createdAt: now,
        updatedAt: now,
      };

      await addDoc(collection(db, COLLECTION_NAMES.ISSUES), issueData);
    } catch (error) {
      console.error('Error creating issue:', error);
      throw error;
    }
  };

  const updateIssue = async (
    issueId: string,
    formData: Partial<{
      name: string;
      description: string;
      priority: 'low' | 'medium' | 'high';
      status: 'open' | 'in-progress' | 'solved';
      relatedRockId: string | null;
      relatedMetricId: string | null;
      assignedToUserId: string | null;
      departmentId: string | null;
      isCompany: boolean;
    }>
  ) => {
    try {
      const issueRef = doc(db, COLLECTION_NAMES.ISSUES, issueId);
      await updateDoc(issueRef, {
        ...formData,
        updatedAt: Timestamp.now(),
      });
    } catch (error) {
      console.error('Error updating issue:', error);
      throw error;
    }
  };

  const resolveIssue = async (issueId: string, resolutionNotes?: string, resolvedByUserId?: string) => {
    try {
      const issueRef = doc(db, COLLECTION_NAMES.ISSUES, issueId);
      await updateDoc(issueRef, {
        status: 'solved',
        solvedAt: Timestamp.now(),
        updatedAt: Timestamp.now(),
        ...(resolutionNotes && { resolutionNotes }),
        ...(resolvedByUserId && { resolvedByUserId }),
      });
    } catch (error) {
      console.error('Error resolving issue:', error);
      throw error;
    }
  };

  const deleteIssue = async (issueId: string) => {
    try {
      const issueRef = doc(db, COLLECTION_NAMES.ISSUES, issueId);
      await deleteDoc(issueRef);
    } catch (error) {
      console.error('Error deleting issue:', error);
      throw error;
    }
  };

  // Filter issues by status
  const getFilteredIssues = (status?: 'open' | 'in-progress' | 'solved') => {
    if (!status) return issues;
    return issues.filter((issue) => issue.status === status);
  };

  return {
    issues,
    actionItems,
    users: eosUsers,
    rocks,
    loading,
    createIssue,
    updateIssue,
    resolveIssue,
    deleteIssue,
    getFilteredIssues,
  };
}

